/**
 * Text Tool - Text Note Creation
 * 
 * Creates 2D text notes.
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface TextGeometryData {
    position: [number, number, number];
    text: string;
    size: number;
}

export interface TextElement extends Element {
    category: 'Text Notes';
    geometryData: TextGeometryData;
}

export class TextTool extends BaseTool {
    id = 'text';
    name = 'Text';
    cursor = 'text';

    private awaitingText = false;
    private textInputHandler: ((e: Event) => void) | null = null;

    protected onActivate(): void {
        // Single listener for the whole app lifetime: the tool is a module
        // singleton, so the handler stays attached and simply ignores events
        // while no entry is pending. The viewport's TextNoteInput dialog
        // commits/cancels through this event.
        if (!this.textInputHandler) {
            this.textInputHandler = ((e: Event) => {
                if (!this.context || !this.awaitingText) return;
                this.awaitingText = false;

                const detail = (e as CustomEvent).detail as { text: string | null } | undefined;
                const pos = this.pendingPosition;
                this.pendingPosition = null;
                this.context.appStore.setToolPreview(null);
                if (detail?.text && pos) {
                    this.createText(pos, detail.text);
                }
            }) as EventListener;
            window.addEventListener('yap:text-note-input', this.textInputHandler);
        }
    }

    private pendingPosition: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;
        if (this.awaitingText) return; // input box already open

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.pendingPosition = snapped;
        this.awaitingText = true;
        // Swap the follow-cursor ghost for the in-place text entry dialog
        // (rendered by the viewport when toolPreview.textEntering is set).
        this.context.appStore.setToolPreview({
            type: 'text',
            position: snapped,
            textEntering: true
        });
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (this.awaitingText) return; // keep the entry dialog in place
        const point = this.getIntersection(event);
        if (point) {
            const snapped = this.snapToGrid(point);
            this.context.appStore.setToolPreview({
                type: 'text',
                position: snapped,
                text: 'Text'
            });
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    deactivate(): void {
        // Drop any open entry request so a late dialog commit (or a tool
        // re-activation) cannot place a stale note.
        this.awaitingText = false;
        this.pendingPosition = null;
        super.deactivate();
    }

    onKeyDown(event: KeyboardEvent): void {
        if (this.awaitingText && event.key === 'Escape') {
            // Cancel entry when focus is outside the input box (typing in the
            // input is stopped there before reaching this handler).
            this.awaitingText = false;
            this.pendingPosition = null;
            this.context?.appStore.setToolPreview(null);
            return;
        }
        super.onKeyDown(event);
    }

    private createText(point: THREE.Vector3, text: string) {
        if (!this.context) return;

        // Options bar settings (BIM Text panel): paper height + leader side.
        const sizeMm = this.context.bimStore.toolProperties.textSizeMm ?? 2.5;
        const leader = this.context.bimStore.toolProperties.textLeader ?? 'None';

        const geometryData: TextGeometryData = {
            position: [point.x, point.y, point.z],
            text,
            size: sizeMm / 1000 // paper meters (2.5 mm default)
        };

        const note: TextElement = {
            id: uuidv4(),
            category: 'Text Notes',
            type: `Text: ${sizeMm}mm Arial`,
            parameters: {
                'Text': text,
                'Size': `${sizeMm} mm`,
                ...(leader !== 'None' ? { 'Leader': leader } : {}),
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(note);
    }
}
